import React, { useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PersonIcon from "@material-ui/icons/Person";
import EmailIcon from "@material-ui/icons/Email";
import CallIcon from "@material-ui/icons/Call";
import MyLocationRoundedIcon from "@material-ui/icons/MyLocationRounded";
import NoteAddRoundedIcon from "@material-ui/icons/NoteAddRounded";
import CloseIcon from "@material-ui/icons/Close";
import BackIcon from "@material-ui/icons/SettingsBackupRestore";
import WarningIcon from "@material-ui/icons/WarningSharp";
import MenuIcon from "@material-ui/icons/Menu";
import moment from "moment";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SkipStopModal from "./SkipStopModal";
import CancelIcon from '@material-ui/icons/Cancel';
import { getAdditionalCustomerDisplayName } from "util/stopStatusUtils";
import {
  buildWarehouseReturnProofUrl,
  findStopRecordByAction,
  getFixStopPictures,
  getFixStopRouteTypeLabel,
  getSupplierLocationIdFromPathwayStop,
  getWarehouseReturnPackages,
  hasWarehouseReturnPackages,
  isWarehouseFixStop,
  getStopAction,
  resolveStopRecordByAction,
  STOP_ACTIONS,
} from "util/warehouseReturnUtils";
import {
  getDeliveryNotePackageCounts,
  getOrderDeliveryNotes,
  hasDeliveredPackagesForOrders,
  orderMatchesStopTab,
  resolveOrderType,
  resolveOrderKind,
  getStopRecordForOrder,
} from "util/pathwayOrderUtils";
import WarehouseReturnPackagesSection from "./WarehouseReturnPackagesSection";
import { formatStopTypeWithLabel } from "util/stopLabelUtils";

const URL = import.meta.env.VITE_API;

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  centerItems: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  arrowButton: {
    padding: 0,
    margin: "0 8px",
    fontSize: "1.5rem",
    fontWeight: "bold",
  },
  disabledArrow: {
    color: "grey !important",
  },
  closeButton: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
  },
  countText: {
    margin: "0 8px",
    fontSize: "1.25rem",
  },

  _box: {
    color: "white",
    display: "flex",
    alignItems: "center",
    padding: "10px 15px",
    border: "1px solid transparent",
    borderRadius: "4px",
    marginBottom: "8px",
    // "&:hover": {
    //     borderColor: "#6F9CEB",
    // },
  },
  _routename: {
    font: "normal normal normal 18px/24px Roboto",
    color: "#F5F5F5",
  },
  _customername: {
    font: "normal normal normal 22px/40px Questrial",
    color: "#F5F5F5",
    display: "block",
    margin: "20px 0",
  },
  _routedetails: {
    display: "flex",
    alignItems: "center",
    color: "#F5F5F5",
    font: "normal normal normal 12px/24px Roboto",
    margin: "20px 0",
    lineHeight: "15px",

    "& svg": {
      marginRight: "10px",
    },

    "& a": {
      color: "#6F9CEB",
    },
  },
  _galleryheading: {
    color: "#F5F5F5",
    marginBottom: "25px",
    marginTop: "60px",
  },
  picture: {
    width: "auto",
    maxHeight: "300px",
    cursor: "pointer",
  },
  _warning: {
    color: `${theme.palette.warning.light} !important`,
  },
  skipAllButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#232323",
    color: "#6F9CEB",
    border: "1px solid #6F9CEB",
    '&:hover': {
      backgroundColor: "#2c2c2c",
      borderColor: "#8EB3F5",
    },
  },
  sectionTitle: {
    color: "#FFF",
    fontSize: "18px",
    fontWeight: 600,
    margin: "32px 0 12px 0",
  },
  orderButtonsRow: {
    display: "flex",
    gap: "10px",
    margin: "8px 0 20px 0",
  },
  orderTabButton: {
    minWidth: 130,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "6px 12px",
  },
  card: {
    background: "#1A1A1A",
    border: "1px solid #2A2A2A",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 6,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  sectionSpacing: {
    marginTop: 40,
    borderTop: "1px solid rgba(255,255,255,0.12)",
    paddingTop: 24
  },
}));

const ImageWithLoader = ({ src, className }) => {
  const [loading, setLoading] = useState(true);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoading(false);
    }
  }, [src]);

  return (
    <Box position="relative">
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      )}
      <img
        ref={imgRef}
        className={className}
        src={src}
        alt=""
        style={loading ? { display: "none" } : undefined}
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    </Box>
  );
};

const Stopview = ({
  route,
  customer,
  supplierLocation,
  type = 'customer',
  onClose,
  onToggleSidebar,
  onSkipStop,
  onPrevStop,
  onNextStop,
  focusedStop,
  index,
  showSkipButton = false,
}) => {
  const [skipModalOpen, setSkipModalOpen] = React.useState(false);
  const [skipAllModalOpen, setSkipAllModalOpen] = React.useState(false);
  const [currentCustomer, setCurrentCustomer] = React.useState(type === 'supplier' ? supplierLocation : customer);
  const classes = useStyles();
  const { i18n, t } = useTranslation();
  const language = i18n.language === "de" ? "de" : "en";
  const rawPathwayStops = Array.isArray(route.pathway) ? route.pathway : [];
  const pathwayStops = rawPathwayStops;
  const supplierLocationId = type === "supplier"
    ? getSupplierLocationIdFromPathwayStop(supplierLocation)
    : null;
  const mainCustomerId = type === "supplier" ? null : customer?.id;
  const totalStops = pathwayStops.length;
  const targetStop = type === "supplier" ? supplierLocation : customer;
  let currentStopIndex = -1;

  if (typeof index === "number" && index >= 0 && index < pathwayStops.length) {
    currentStopIndex = index;
  } else {
    currentStopIndex = pathwayStops.findIndex((p) => {
      if (targetStop && p === targetStop) return true;
      if (mainCustomerId != null && p?.id === mainCustomerId) return true;
      if (type === "supplier" && supplierLocationId != null && p?.supplier_location_id === supplierLocationId) return true;
      return false;
    });
  }

  if (currentStopIndex === -1) {
    currentStopIndex = 0;
  }

  const pathwayStop = pathwayStops[currentStopIndex] ?? (type === "supplier" ? supplierLocation : customer);
  const isFixStopView = isWarehouseFixStop(pathwayStop);
  const isSupplierPathwayStop =
    type === "supplier" ||
    pathwayStop?.id == null;
  const deliveryTimingConfig = route?.delivery_timing_config;
  const scanningCodesEnabled =
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== false &&
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== 0 &&
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== "0";
  const stopRecordContext = {
    customerId: type === "supplier" ? null : mainCustomerId,
    pathwayStop,
    pathwayIndex: currentStopIndex,
  };
  const deliveryStopRecord = resolveStopRecordByAction(
    route,
    stopRecordContext,
    STOP_ACTIONS.DELIVERY
  );
  const pickupStopRecord = resolveStopRecordByAction(
    route,
    stopRecordContext,
    STOP_ACTIONS.PICKUP
  );
  const isPickupStop =
    pathwayStop?.route_type !== "end_point" &&
    pathwayStop?.route_type !== "before_end" &&
    pathwayStop?.route_type !== "after_end";

  const returnStopRecord = resolveStopRecordByAction(
    route,
    stopRecordContext,
    STOP_ACTIONS.RETURN
  );
  const warehouseReturnStopRecord = resolveStopRecordByAction(
    route,
    stopRecordContext,
    STOP_ACTIONS.RETURN_TO_WAREHOUSE
  );
  const isWarehouseReturnStop =
    pathwayStop?.route_type === "end_point" ||
    pathwayStop?.route_type === "before_end" ||
    pathwayStop?.route_type === "after_end";

  const showWarehouseReturns =
    hasWarehouseReturnPackages(pathwayStop) ||
    (isWarehouseReturnStop &&
      (warehouseReturnStopRecord != null || returnStopRecord != null || pathwayStop?.completed_at != null));
  const deliveryStop = pickupStopRecord || deliveryStopRecord;
  const returnStop = returnStopRecord;
  const stop =
    warehouseReturnStopRecord ||
    returnStopRecord ||
    pickupStopRecord ||
    deliveryStopRecord;
  const deliveryPictures = getFixStopPictures(pickupStopRecord, deliveryStopRecord);
  const returnPictures = getFixStopPictures(returnStopRecord);
  const allReturnPackages = getWarehouseReturnPackages(pathwayStop);
  const hasReturnedPackages =
    allReturnPackages.length > 0 &&
    allReturnPackages.some(
      (p) => p?.status === "delivered" || p?.returned_at != null
    );

  const targetSupplierLocationId =
    getSupplierLocationIdFromPathwayStop(pathwayStop) ??
    (type === "supplier" ? getSupplierLocationIdFromPathwayStop(supplierLocation) : null);

  const warehouseReturnStop =
    warehouseReturnStopRecord ||
    returnStopRecord;
  const warehouseReturnPictures = getFixStopPictures(
    warehouseReturnStopRecord,
    returnStopRecord
  );
  const warehouseReturnProofUrl =
    warehouseReturnStop && (hasReturnedPackages || hasWarehouseReturnPackages(pathwayStop))
      ? buildWarehouseReturnProofUrl(
        URL,
        route.id,
        pathwayStop,
        language,
        localStorage.getItem("token")
      )
      : null;
  const warehouseReturnTime =
    warehouseReturnStopRecord?.time ||
    returnStopRecord?.time ||
    (warehouseReturnStop ? pathwayStop?.completed_at : null) ||
    null;
  const returnDriverName =
    warehouseReturnStopRecord?.driver_name ||
    returnStopRecord?.driver_name ||
    (warehouseReturnStop ? (pathwayStop?.driver_name || route?.driver_name || route?.Driver?.name) : null);
  const returnDriverPhone =
    warehouseReturnStopRecord?.driver_phone ||
    returnStopRecord?.driver_phone ||
    (warehouseReturnStop ? (pathwayStop?.driver_phone || route?.driver_phone || route?.Driver?.phone) : null);
  const returnCoordinates =
    warehouseReturnStopRecord?.location?.coordinates ||
    returnStopRecord?.location?.coordinates ||
    (warehouseReturnStop
      ? (pathwayStop?.latitude != null && pathwayStop?.longitude != null
        ? [pathwayStop.longitude, pathwayStop.latitude]
        : supplierLocation?.latitude != null && supplierLocation?.longitude != null
          ? [supplierLocation.longitude, supplierLocation.latitude]
          : pathwayStop?.location?.coordinates || null)
      : null);
  const returnGoodsBack =
    warehouseReturnStopRecord?.goods_back ||
    returnStopRecord?.goods_back;
  const returnReason =
    warehouseReturnStopRecord?.reason ||
    returnStopRecord?.reason;

  const safeAdditionalCustomers =
    (type === "supplier"
      ? supplierLocation?.additionalCustomers
      : customer?.additionalCustomers) || [];
  const combinedCustomers =
    type === "supplier"
      ? [supplierLocation, ...safeAdditionalCustomers]
      : [customer, ...safeAdditionalCustomers];
  const hiddenAdditionalOrderIds = safeAdditionalCustomers
    .filter((cust) => cust?.isNameHidden)
    .flatMap((cust) => cust?.Orders || [])
    .map((o) => o.id)
    .filter((id) => id != null);

  const getOrdersForCustomer = (cust) => {
    let orders = cust?.Orders || [];

    if (cust?.id) {
      orders = orders.filter(
        (o) => !o?.customer_id || o.customer_id === cust.id
      );
    }

    if (!cust?.isNameHidden && hiddenAdditionalOrderIds.length) {
      orders = orders.filter(
        (o) => !hiddenAdditionalOrderIds.includes(o.id)
      );
    }

    orders = orders.filter((o) => !o?.removed_at);

    return orders;
  };

  const getAllOrdersForStop = () =>
    combinedCustomers.flatMap((cust) => getOrdersForCustomer(cust));

  const isPickupAction =
    getStopAction(deliveryStop) === STOP_ACTIONS.PICKUP ||
    (getStopAction(deliveryStop) === null && isSupplierPathwayStop && isPickupStop && !getAllOrdersForStop().some(
      (o) => resolveOrderKind(o) === "delivery" && getOrderDeliveryNotes(o).length > 0
    ));

  const hasReservation = Number(stop?.reservation_amount) > 0 || Number(pathwayStop?.reservation_amount) > 0;
  const hasDeliveryPackagesDelivered = hasDeliveredPackagesForOrders(
    getAllOrdersForStop(),
    "delivery",
    pathwayStop,
    { scanningCodesEnabled, route, pathwayIndex: currentStopIndex }
  ) || hasReservation;
  const hasReturnPackagesDelivered = hasDeliveredPackagesForOrders(
    getAllOrdersForStop(),
    "return",
    pathwayStop,
    { scanningCodesEnabled, route, pathwayIndex: currentStopIndex }
  );

  const handleSkipStop = (reason) => {
    onSkipStop(route.id, currentCustomer?.id, reason);
  };
  const [stopTab, setStopTab] = React.useState("delivery");

  const handleSkipAllStops = (reason) => {
    const customerIds = combinedCustomers
      .filter(
        (cust) =>
          cust.id !== null &&
          !(route.Stops && route.Stops.some((s) => s.customer_id === cust.id)) &&
          !cust.Orders?.some((o) => o.delivered_at) &&
          !cust.Orders?.some((o) => o.removed_at) &&
          !cust.skipped_at
      )
      .map((cust) => cust.id);

    customerIds.forEach((id) => {
      onSkipStop(route.id, id, reason, true);
    });

    setSkipAllModalOpen(false);
  };

  const renderPhotos = (pictures) => {
    if (!pictures?.length) {
      return null;
    }

    return (
      <Box mt={2}>
        <Typography variant="h6" className={classes._galleryheading}>
          {t("Photos")}
        </Typography>
        <Carousel
          infiniteLoop={true}
          autoPlay={false}
          showArrows={true}
          showIndicators={false}
          showThumbs={false}
          onClickItem={(i) => window.open(pictures[i], "_blank")}
          statusFormatter={(c, total) => `${c} ${t("of")} ${total}`}
        >
          {pictures.map((p) => (
            <ImageWithLoader key={p} className={classes.picture} src={p} />
          ))}
        </Carousel>
      </Box>
    );
  };

  let isStopCustomer = false;
  const renderCustomerInfo = (cust, index) => {
    const customerStop = route.Stops ? route.Stops.find((s) => s.customer_id === cust.id) : null;

    if (cust?.isNameHidden) {
      return null;
    }

    if (cust?.route_type === "customer" || (cust?.id != null && !isWarehouseFixStop(cust))) {
      isStopCustomer = true;
    }

    return (
      <Box key={cust.id || index} mt={index > 0 ? 4 : 0}>
        {index > 0 && (
          <Typography variant="subtitle1" style={{ color: "#F5F5F5", marginBottom: "10px" }}>
            {t("Additional Customer")} {index}
          </Typography>
        )}

        <Typography
          style={{
            color: "#6F9CEB",
            fontSize: "16px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          {formatStopTypeWithLabel(cust, route, currentStopIndex, t)}
        </Typography>

        {index === 0 && (
          <Typography className={classes._customername}>
            {cust.isNameHidden ? "-" : cust.name}
          </Typography>
        )}

        {index > 0 && (
          <Typography className={classes._customername} style={{ fontSize: "20px", margin: "10px 0" }}>
            {getAdditionalCustomerDisplayName(cust) || "-"}
          </Typography>
        )}

        {showSkipButton &&
          !customerStop &&
          !cust.Orders?.some((o) => o.delivered_at) &&
          !cust.Orders?.some((o) => o.removed_at) &&
          !cust.skipped_at && (
            <Box mb={2}>
              <Box textAlign="right">
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setSkipModalOpen(true);
                    setCurrentCustomer(cust);
                  }}
                >
                  {t("Skip Stop")}
                </Button>
              </Box>
            </Box>
          )}

        <Box>
          <div className={classes._routedetails}>
            <LocationOnIcon />
            &nbsp;
            <Typography>
              {cust.street} {cust.street_number}, {cust.city} ({cust.zipcode}), {cust.country}
            </Typography>
          </div>
          <div className={classes._routedetails}>
            <PersonIcon />
            &nbsp;
            <Typography>
              {t(cust.contact_salutation)} {cust.contact_name} {cust.contact_surname}
            </Typography>
          </div>
          <div className={classes._routedetails}>
            <EmailIcon />
            &nbsp;
            <Typography>{cust.email}</Typography>
          </div>
          <div className={classes._routedetails}>
            <CallIcon />
            &nbsp;
            <Typography>{cust.phone}</Typography>
          </div>
          {cust.skipped_at && (
            <div className={classes._routedetails}>
              <WarningIcon className={classes._warning} />
              &nbsp;
              <Typography className={classes._warning}>
                {t("Skipped At")} {moment(cust.skipped_at).format("DD.MM.YYYY HH:mm")}
              </Typography>
            </div>
          )}
          {cust.skipping_reason && (
            <div className={classes._routedetails}>
              <WarningIcon className={classes._warning} />
              &nbsp;
              <Typography className={classes._warning}>{t(cust.skipping_reason)}</Typography>
            </div>
          )}
        </Box>

        {(() => {
          const ordersForStop = getOrdersForCustomer(cust);
          const isCustomer = cust?.id != null && !isWarehouseFixStop(cust);
          const isSupplierStop = !isCustomer;
          const isHandoverStop = isSupplierStop;
          const ordersWithNotes = ordersForStop.filter(
            (o) => getOrderDeliveryNotes(o).length > 0
          );

          const isCustomerStop = isCustomer;
          const showHandoverSection = isHandoverStop && ordersWithNotes.length > 0;
          const showCustomerOrdersSection =
            isCustomerStop && ordersForStop.length > 0;

          const showWarehouseReturnsTab =
            index === 0 && hasWarehouseReturnPackages(pathwayStop);
          const isWarehouseReturnView = index === 0 && isWarehouseReturnStop;
          const hasStopRecordsOrPhotos =
            index === 0 &&
            (!!stop ||
              deliveryPictures.length > 0 ||
              returnPictures.length > 0 ||
              warehouseReturnPictures.length > 0);

          const showStopPackagesSection =
            showHandoverSection ||
            showCustomerOrdersSection ||
            showWarehouseReturnsTab ||
            isWarehouseReturnView ||
            hasStopRecordsOrPhotos;

          if (!showStopPackagesSection) return null;

          const ordersSource = showHandoverSection ? ordersWithNotes : ordersForStop;
          const hasDeliveryOrders = isSupplierStop
            ? (isPickupStop || ordersSource.some((o) => orderMatchesStopTab(o, "delivery") && getOrderDeliveryNotes(o).length > 0))
            : ordersSource.some(
              (o) => orderMatchesStopTab(o, "delivery") && getOrderDeliveryNotes(o).length > 0
            );
          const hasReturnOrders = !isSupplierStop && ordersSource.some((o) => orderMatchesStopTab(o, "return"));
          const ordersForTab = ordersSource.filter((o) => orderMatchesStopTab(o, stopTab));
          const visibleTabCount =
            Number(hasDeliveryOrders) +
            Number(hasReturnOrders) +
            Number(showWarehouseReturnsTab);
          const isPickupLabel = isSupplierStop && isPickupStop && !ordersSource.some(
            (o) => resolveOrderKind(o) === "delivery" && getOrderDeliveryNotes(o).length > 0
          );
          return (
            <>
              {(showHandoverSection || showCustomerOrdersSection) && (
                <Typography variant="h6" className={classes._galleryheading} style={{ marginBottom: "10px" }}>
                  {t(showHandoverSection ? "Handover Orders" : "Orders")}
                </Typography>
              )}

              {visibleTabCount > 1 && (
                <Box mb={2} display="flex" gap="8px" flexWrap="wrap">
                  {hasDeliveryOrders && (
                    <Button
                      variant={stopTab === "delivery" ? "contained" : "outlined"}
                      color="primary"
                      size="small"
                      onClick={() => setStopTab("delivery")}
                    >
                      {t(isPickupLabel ? "Pickup" : "Deliveries")}
                    </Button>
                  )}

                  {hasReturnOrders && (
                    <Button
                      variant={stopTab === "return" ? "contained" : "outlined"}
                      color="primary"
                      size="small"
                      onClick={() => setStopTab("return")}
                    >
                      {t("Returns")}
                    </Button>
                  )}

                  {showWarehouseReturnsTab && (
                    <Button
                      variant={stopTab === "warehouse_return" ? "contained" : "outlined"}
                      color="primary"
                      size="small"
                      onClick={() => setStopTab("warehouse_return")}
                    >
                      {t("warehouse_return.section_title")}
                    </Button>
                  )}
                </Box>
              )}

              {stopTab === "warehouse_return" && (showWarehouseReturnsTab || isWarehouseReturnView) && (
                <>
                  {
                    hasWarehouseReturnPackages(pathwayStop) && (
                      <WarehouseReturnPackagesSection
                        pathwayStop={pathwayStop}
                        embedded
                        isStopMarked={!!warehouseReturnStop}
                      />
                    )
                  }
                  {renderPhotos(warehouseReturnPictures)}
                  {!!warehouseReturnStop && (
                    <Box mt={4}>
                      <Typography variant="h6" className={classes._galleryheading}>
                        {t(
                          hasWarehouseReturnPackages(pathwayStop)
                            ? "warehouse_return.completion_title"
                            : getFixStopRouteTypeLabel(pathwayStop?.route_type, t)
                        )}
                      </Typography>
                      <div>
                        <div className={classes._routedetails}>
                          <NoteAddRoundedIcon />
                          &nbsp;
                          <Typography>
                            {
                              warehouseReturnTime
                                ? moment(warehouseReturnTime).format("DD.MM.YYYY HH:mm")
                                : "-"
                            }
                            {
                              warehouseReturnProofUrl && (
                                <>
                                  <br />
                                  <a
                                    href={warehouseReturnProofUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      display: "block",
                                      marginBottom: "4px",
                                      textDecoration: "none",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {t("proof of returned order")}
                                  </a>
                                </>
                              )
                            }
                          </Typography >
                        </div >

                        {!!returnGoodsBack && (
                          <div className={classes._routedetails}>
                            <BackIcon />
                            <Typography>{t(returnReason)}</Typography>
                          </div>
                        )}

                        {
                          returnCoordinates && (
                            <div className={classes._routedetails}>
                              <MyLocationRoundedIcon />
                              &nbsp;
                              <Typography>
                                {returnCoordinates[1]},{" "}
                                {returnCoordinates[0]}
                              </Typography>
                            </div>
                          )
                        }

                        {
                          returnDriverName && (
                            <div className={classes._routedetails}>
                              <PersonIcon />
                              &nbsp;
                              <Typography>{returnDriverName}</Typography>
                            </div>
                          )
                        }

                        {
                          returnDriverPhone && (
                            <div className={classes._routedetails}>
                              <CallIcon />
                              &nbsp;
                              <Typography>{returnDriverPhone}</Typography>
                            </div>
                          )
                        }
                      </div >
                    </Box >
                  )}
                </>
              )}

              {
                (stopTab === "delivery" || stopTab === "return") &&
                ordersForTab.map((order, i) => {
                  const deliveryNotes = getOrderDeliveryNotes(order);
                  if (!deliveryNotes.length) return null;

                  return (
                    <Box key={order.id ?? i}>
                      <Box mt={1}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          style={{
                            padding: "10px 14px",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "white",
                            background: "#000",
                            borderBottom: "1px solid rgba(255,255,255,0.15)",
                            marginTop: "20px",
                          }}
                        >
                          <span>
                            {order.description ||
                              (isWarehouseFixStop(cust) ||
                                cust.route_type === "before_end" ||
                                cust.route_type === "after_end"
                                ? t("Warehouse")
                                : resolveOrderKind(order) === "pickup"
                                  ? t("Pickup")
                                  : resolveOrderKind(order) === "return"
                                    ? t("Return")
                                    : t("delivery note(s)"))}
                          </span>
                          <span>{t("Completed")}</span>
                        </Box>

                        {deliveryNotes.map((note, noteIdx) => {
                          const stopRecordForOrder = getStopRecordForOrder(route, order, pathwayStop, currentStopIndex);
                          const { delivered, total } = getDeliveryNotePackageCounts(note, order, stopRecordForOrder, { scanningCodesEnabled });

                          return (
                            <Box
                              key={note.id ?? noteIdx}
                              display="flex"
                              justifyContent="space-between"
                              style={{
                                padding: "14px 14px",
                                background: noteIdx % 2 === 0 ? "#1F1F1F" : "#5A5A5A",
                                color: "white",
                                fontSize: "14px",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              <span>{note.delivery_note_number}</span>
                              <span style={{ fontWeight: 600 }}>
                                {delivered}/{total}
                              </span>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })
              }

              {
                (stopTab === "delivery" || stopTab === "return") && ordersForTab.length > 0 && (
                  <>
                    {stopTab === "delivery" && renderPhotos(deliveryPictures)}
                    {stopTab === "return" && renderPhotos(returnPictures)}
                  </>
                )
              }
            </>
          );
        })()}

        {index < combinedCustomers.length - 1 && <Box mt={4} mb={2} borderBottom="1px solid rgba(255,255,255,0.2)" />}
      </Box >
    );
  };


  useEffect(() => {
    const stopOrders = getAllOrdersForStop();
    const isCustomer = pathwayStop?.id != null && !isWarehouseFixStop(pathwayStop);
    const isSupplierStop = !isCustomer;
    const hasDeliveryOrders = isSupplierStop
      ? (isPickupStop || stopOrders.some((o) => orderMatchesStopTab(o, "delivery") && getOrderDeliveryNotes(o).length > 0))
      : stopOrders.some((o) => orderMatchesStopTab(o, "delivery") && getOrderDeliveryNotes(o).length > 0);
    const hasReturnOrders = !isSupplierStop && stopOrders.some((o) => orderMatchesStopTab(o, "return"));

    if (hasDeliveryOrders && (deliveryStopRecord || pickupStopRecord)) {
      setStopTab("delivery");
    } else if (hasReturnOrders && returnStopRecord) {
      setStopTab("return");
    } else if (showWarehouseReturns) {
      setStopTab("warehouse_return");
    } else if (hasDeliveryOrders) {
      setStopTab("delivery");
    } else if (hasReturnOrders) {
      setStopTab("return");
    }
  }, [
    route?.id,
    currentStopIndex,
    showWarehouseReturns,
    deliveryStopRecord,
    pickupStopRecord,
    returnStopRecord,
    isFixStopView,
    type,
    pathwayStop,
    isPickupStop,
  ]);

  return (
    <Box>
      <Box className={classes.headerContainer}>
        <IconButton
          onClick={onToggleSidebar}
          aria-label={t("Toggle sidebar")}
        >
          <MenuIcon />
        </IconButton>
        <Box className={classes.centerItems}>
          <IconButton
            onClick={onPrevStop}
            disabled={currentStopIndex === 0}
            aria-label={t("Previous Stop")}
            className={classes.arrowButton}
          >
            <ChevronLeftIcon className={currentStopIndex === 0 ? classes.disabledArrow : ""} />
          </IconButton>
          <Typography variant="body1" className={classes.countText}>
            {currentStopIndex + 1} / {totalStops}
          </Typography>
          <IconButton
            onClick={onNextStop}
            disabled={currentStopIndex === totalStops - 1}
            aria-label={t("Next Stop")}
            className={classes.arrowButton}
          >
            <ChevronRightIcon className={currentStopIndex === totalStops - 1 ? classes.disabledArrow : ""} />
          </IconButton>
        </Box>
        <IconButton onClick={onClose} className={classes.closeButton} aria-label={t("Close")}>
          <CloseIcon />
        </IconButton>
      </Box>
      {combinedCustomers.length && (
        <Box p={2}>
          <Box>
            <Typography className={classes._routename}>
              T{route.uuid} {route.Tour?.name}
            </Typography>

            {showSkipButton &&
              combinedCustomers.length > 1 &&
              combinedCustomers.some(
                (cust) =>
                  cust.id !== null &&
                  !(route.Stops && route.Stops.some((s) => s.customer_id === cust.id)) &&
                  !cust.Orders?.some((o) => o.delivered_at) &&
                  !cust.Orders?.some((o) => o.removed_at) &&
                  !cust.skipped_at
              ) && (
                <Box textAlign="right" mt={1} mb={2}>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<CancelIcon />}
                    className={classes.skipAllButton}
                    onClick={() => setSkipAllModalOpen(true)}
                  >
                    {t("Skip All Orders")}
                  </Button>
                </Box>
              )}
          </Box>

          {combinedCustomers.map((cust, idx) => renderCustomerInfo(cust, idx))}

          {/* Stop completion details */}
          {!!stop && getAllOrdersForStop().length > 0 && (
            <>
              {(deliveryStop || returnStopRecord) && (
                <Box mt={4}>
                  {stopTab === "delivery" && deliveryStop && (
                    <Box>
                      <Typography variant="h6" className={classes._galleryheading}>
                        {t(isPickupAction ? "Pickup" : "Delivery")}
                      </Typography>
                      <div>
                        <div className={classes._routedetails}>
                          <NoteAddRoundedIcon />
                          &nbsp;
                          <Typography>
                            {moment(deliveryStop?.time || stop.time).format("DD.MM.YYYY HH:mm")}
                            {isStopCustomer && !!deliveryStop && (
                              <>
                                <br />
                                <a
                                  href={`${URL}routes/${route.id}/proof-of-delivery/${(type === 'supplier'
                                    ? getSupplierLocationIdFromPathwayStop(supplierLocation)
                                    : customer?.id)}/${language}?taira=${localStorage.getItem("token")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ display: "block", marginBottom: "4px", textDecoration: "none", cursor: "pointer" }}
                                >
                                  {t("proof of delivery")}
                                </a>
                              </>)
                            }
                          </Typography>
                        </div>
                        {combinedCustomers.some((cust) =>
                          cust.Orders?.some(o =>
                            o.DeliveryNotes?.some(dn =>
                              dn.Packages?.some(p => p.is_package_lost === true)
                            )
                          )
                        ) && (
                            <div className={classes._routedetails}>
                              <NoteAddRoundedIcon />
                              <Typography>
                                {isStopCustomer && (
                                  <a
                                    href={`${URL}routes/${route.id}/lost-packages/${(type === 'supplier'
                                      ? getSupplierLocationIdFromPathwayStop(supplierLocation)
                                      : customer?.id)}/${language}?taira=${localStorage.getItem("token")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ display: "block", textDecoration: "none", color: "#6F9CEB" }}
                                  >
                                    {t("lost report")}
                                  </a>)
                                }
                              </Typography>
                            </div>
                          )}

                        {!!deliveryStop?.goods_back &&
                          deliveryStop?.order_type !== "return" &&
                          getStopAction(deliveryStop) !== STOP_ACTIONS.PICKUP &&
                          getStopAction(deliveryStop) !== STOP_ACTIONS.RETURN && (
                            <div className={classes._routedetails}>
                              <BackIcon />
                              <Typography>{t(deliveryStop.reason)}</Typography>
                            </div>
                          )}

                        <div className={classes._routedetails}>
                          <MyLocationRoundedIcon />
                          &nbsp;
                          <Typography>
                            {deliveryStop?.location?.coordinates?.[1]}, {deliveryStop?.location?.coordinates?.[0]}
                          </Typography>
                        </div>

                        <div className={classes._routedetails}>
                          <PersonIcon />
                          &nbsp;
                          <Typography>{deliveryStop?.driver_name}</Typography>
                        </div>

                        <div className={classes._routedetails}>
                          <CallIcon />
                          &nbsp;
                          <Typography>{deliveryStop?.driver_phone}</Typography>
                        </div>
                      </div>
                    </Box>
                  )}

                  {stopTab === "return" && returnStopRecord && (
                    <Box>
                      <Typography variant="h6" className={classes._galleryheading}>
                        {t("Return")}
                      </Typography>
                      <div>
                        <div className={classes._routedetails}>
                          <NoteAddRoundedIcon />
                          &nbsp;
                          <Typography>
                            {moment(returnStopRecord?.time || stop.time).format("DD.MM.YYYY HH:mm")}
                            {isStopCustomer && !!returnStopRecord && (
                              <>
                                <br />
                                <a
                                  href={`${URL}routes/${route.id}/proof-of-return/${(type === 'supplier'
                                    ? getSupplierLocationIdFromPathwayStop(supplierLocation)
                                    : customer?.id)}/${language}?taira=${localStorage.getItem("token")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ display: "block", marginBottom: "4px", textDecoration: "none", cursor: "pointer" }}
                                >
                                  {t("proof of return")}
                                </a>
                              </>)}
                          </Typography>
                        </div>

                        {!!returnStopRecord?.goods_back && (
                          <div className={classes._routedetails}>
                            <BackIcon />
                            <Typography>{t(returnStopRecord.reason)}</Typography>
                          </div>
                        )}

                        <div className={classes._routedetails}>
                          <MyLocationRoundedIcon />
                          &nbsp;
                          <Typography>
                            {returnStopRecord?.location?.coordinates?.[1]}, {returnStopRecord?.location?.coordinates?.[0]}
                          </Typography>
                        </div>

                        <div className={classes._routedetails}>
                          <PersonIcon />
                          &nbsp;
                          <Typography>{returnStopRecord?.driver_name}</Typography>
                        </div>

                        <div className={classes._routedetails}>
                          <CallIcon />
                          &nbsp;
                          <Typography>{returnStopRecord?.driver_phone}</Typography>
                        </div>
                      </div>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Existing Skip Modal */}
      {showSkipButton && (
        <SkipStopModal
          open={skipModalOpen}
          onClose={() => setSkipModalOpen(false)}
          onSkipStop={handleSkipStop}
          customerName={currentCustomer?.name || "-"}
          showStopOptions={currentCustomer?.id ? false : true}
        />
      )}

      {/* New Skip All Modal */}
      {showSkipButton && (
        <SkipStopModal
          open={skipAllModalOpen}
          onClose={() => setSkipAllModalOpen(false)}
          onSkipStop={handleSkipAllStops}
          customerName={t("All Remaining Stops")} // Descriptive name for the modal
        />
      )}
    </Box>
  );
};

export default Stopview;
